import { useState, useEffect } from "react";
import {
  doc, updateDoc, getDoc, arrayUnion, onSnapshot, increment
} from "firebase/firestore";
import { auth, db } from "../firebase";

function showPointsFailureNotice() {
  const el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:rgba(26,23,20,0.85);color:white;padding:10px 20px;border-radius:100px;font-size:13px;font-family:"DM Sans",sans-serif;z-index:9999;pointer-events:none;white-space:nowrap;';
  el.textContent = "Points couldn't be added right now — we'll sort it out.";
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}

export async function awardPoints(uid, amount, reason) {
  const write = () => updateDoc(doc(db, "users", uid), {
    points: increment(amount),
    pointsHistory: arrayUnion({ amount, reason, date: new Date().toISOString() }),
  });
  try {
    await write();
  } catch (err) {
    console.error("Error awarding points (attempt 1):", err);
    try {
      await write();
    } catch (retryErr) {
      console.error("Error awarding points (attempt 2):", retryErr);
      showPointsFailureNotice();
    }
  }
}

// ── Pure debt calculation ────────────────────────────────────────────────────
// Builds a simplified list of who owes who, after netting out mutual debts
// and excluding any (from, to) pair that already has a settled record.
function calculateDebts(expenses, members, settledDebts) {
  const balances = {};
  members.forEach(m => { balances[m.uid] = 0; });

  expenses.forEach(exp => {
    balances[exp.paidByUid] = (balances[exp.paidByUid] || 0) + exp.amount;
    Object.entries(exp.splits || {}).forEach(([uid, amt]) => {
      balances[uid] = (balances[uid] || 0) - amt;
    });
  });

  // Apply settled debts as balance adjustments before the greedy pass,
  // so a settled pair's new debts aren't silently hidden.
  (settledDebts || []).forEach(s => {
    balances[s.from] = (balances[s.from] || 0) + s.amount;
    balances[s.to]   = (balances[s.to]   || 0) - s.amount;
  });

  const getName = uid => {
    const m = members.find(m => m.uid === uid);
    return m?.name?.split(" ")[0] || "Former member";
  };

  const creditors = []; // owed money (positive balance)
  const debtors = [];   // owe money (negative balance)

  Object.entries(balances).forEach(([uid, bal]) => {
    if (bal > 0.01)  creditors.push({ uid, name: getName(uid), amount: bal });
    if (bal < -0.01) debtors.push({ uid, name: getName(uid), amount: -bal });
  });

  creditors.sort((a, b) => b.amount - a.amount);
  debtors.sort((a, b) => b.amount - a.amount);

  // Deep-copy so we can mutate amounts during greedy pass
  const c = creditors.map(x => ({ ...x }));
  const d = debtors.map(x => ({ ...x }));
  const result = [];

  let ci = 0, di = 0;
  while (ci < c.length && di < d.length) {
    const creditor = c[ci];
    const debtor   = d[di];
    const amount   = Math.min(creditor.amount, debtor.amount);

    if (amount > 0.01) {
      result.push({
        from:     debtor.uid,
        fromName: debtor.name,
        to:       creditor.uid,
        toName:   creditor.name,
        amount:   parseFloat(amount.toFixed(2)),
      });
    }

    creditor.amount -= amount;
    debtor.amount   -= amount;
    if (creditor.amount < 0.01) ci++;
    if (debtor.amount  < 0.01) di++;
  }

  return result;
}

export default function Expenses({ trip, onBack }) {
  const [expenses,      setExpenses]      = useState([]);
  const [members,       setMembers]       = useState([]);
  const [memberUids,    setMemberUids]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [showForm,      setShowForm]      = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [activeTab,     setActiveTab]     = useState("expenses");
  const [settledDebts,  setSettledDebts]  = useState(trip.settledDebts || []);
  const [settling,      setSettling]      = useState(null); // key of debt being settled
  const [settleError,   setSettleError]   = useState(null);
  const [deleteError,   setDeleteError]   = useState(null);
  const [addError,      setAddError]      = useState(null);
  const [currentUid,    setCurrentUid]    = useState(auth.currentUser?.uid || '');
  const [form, setForm] = useState({
    description: "",
    amount: "",
    category: "Food",
    paidBy: auth.currentUser?.uid || '',
    splitType: "equal",
    customSplits: {},
  });

  useEffect(() => {
    if (auth.currentUser) setCurrentUid(auth.currentUser.uid);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "trips", trip.id), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSettledDebts(data.settledDebts || []);
        setExpenses(data.expenses || []);
      }
    });
    return () => unsub();
  }, [trip.id]);

  const fetchData = async () => {
    setLoading(true);
    if (!auth.currentUser) {
      setMembersLoaded(true);
      setLoading(false);
      return;
    }
    try {
      const tripSnap = await getDoc(doc(db, "trips", trip.id));
      const tripData = tripSnap.exists() ? tripSnap.data() : {};
      const uids = tripData.memberUids || trip.memberUids || [auth.currentUser.uid];

      setMemberUids(uids);
      setSettledDebts(tripData.settledDebts || []);

      const memberProfiles = uids.map(uid => ({
        uid,
        name: uid === auth.currentUser?.uid ? "You" : "Traveler",
      }));

      for (let i = 0; i < uids.length; i++) {
        try {
          const userSnap = await getDoc(doc(db, "users", uids[i]));
          if (userSnap.exists() && userSnap.data().name) {
            memberProfiles[i].name = userSnap.data().name;
          }
        } catch (e) {
          // keep placeholder
        }
      }

      setMembers(memberProfiles);
      setMembersLoaded(true);

      setExpenses(tripData.expenses || []);

    } catch (err) {
      console.error("fetchData error:", err);
      setMembers([{ uid: currentUid, name: "You" }]);
      setMemberUids([currentUid]);
      setMembersLoaded(true);
    }
    setLoading(false);
  };

  const handleAddExpense = async () => {
    if (!form.description || !form.amount) return;
    if (!auth.currentUser) return;
    setAddError(null);
    setSubmitting(true);
    let saved = false;
    try {
      const amount = parseFloat(form.amount);
      const uids = memberUids.length > 0 ? memberUids : [auth.currentUser.uid];

      let splits = {};
      if (form.splitType === "equal") {
        const totalCents = Math.round(amount * 100);
        const n = uids.length;
        const base = Math.floor(totalCents / n);
        const remainder = totalCents - base * n;
        uids.forEach((uid, i) => {
          splits[uid] = (base + (i < remainder ? 1 : 0)) / 100;
        });
      } else {
        splits = form.customSplits;
        const splitTotal = Object.values(splits).reduce((s, v) => s + (parseFloat(v) || 0), 0);
        if (Math.abs(splitTotal - amount) > 0.01) {
          setAddError(`Custom splits must add up to $${amount.toFixed(2)} (currently $${splitTotal.toFixed(2)}).`);
          setSubmitting(false);
          return;
        }
      }

      const expense = {
        id: crypto.randomUUID(),
        description: form.description,
        amount,
        category: form.category,
        paidByUid: form.paidBy,
        paidByName: getMemberName(form.paidBy),
        splits,
        splitType: form.splitType,
        createdByUid: auth.currentUser.uid,
        createdAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, "trips", trip.id), {
        expenses: arrayUnion(expense),
      });

      await awardPoints(auth.currentUser.uid, 10, "Added trip expense");
      saved = true;
    } catch (err) {
      console.error(err);
      setAddError("Failed to add expense. Please try again.");
    }
    if (saved) {
      setForm({
        description: "",
        amount: "",
        category: "Food",
        paidBy: currentUid,
        splitType: "equal",
        customSplits: {},
      });
      setShowForm(false);
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    setDeleteError(null);
    const updated = expenses.filter(e => e.id !== id);
    try {
      await updateDoc(doc(db, "trips", trip.id), { expenses: updated });
    } catch (err) {
      console.error("Expenses.handleDelete failed:", err);
      setDeleteError("Failed to delete expense. Please try again.");
    }
  };

  const handleSettle = async (debt) => {
    if (!auth.currentUser) return;
    const key = `${debt.from}_${debt.to}`;
    setSettling(key);
    setSettleError(null);
    const record = {
      from:      debt.from,
      to:        debt.to,
      amount:    debt.amount,
      settledAt: new Date(),
      settledBy: auth.currentUser.uid,
    };
    const previousSettledDebts = settledDebts;
    const optimistic = [...settledDebts, record];
    setSettledDebts(optimistic);
    try {
      await updateDoc(doc(db, "trips", trip.id), { settledDebts: arrayUnion(record) });
    } catch (err) {
      console.error(err);
      setSettledDebts(previousSettledDebts);
      setSettleError("Failed to settle. Please try again.");
    }
    setSettling(null);
  };

  const calculateBalances = () => {
    const balances = {};
    memberUids.forEach(uid => { balances[uid] = 0; });
    expenses.forEach(exp => {
      balances[exp.paidByUid] = (balances[exp.paidByUid] || 0) + exp.amount;
      Object.entries(exp.splits || {}).forEach(([uid, amount]) => {
        balances[uid] = (balances[uid] || 0) - amount;
      });
    });
    // Apply settled debts so this tab stays consistent with Settle Up
    (settledDebts || []).forEach(s => {
      balances[s.from] = (balances[s.from] || 0) + s.amount;
      balances[s.to]   = (balances[s.to]   || 0) - s.amount;
    });
    return balances;
  };

  const getMemberName = (uid) => {
    const m = members.find(m => m.uid === uid);
    return m?.name?.split(" ")[0] || "Former member";
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const uid = currentUid;
  const totalSpent = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const balances = calculateBalances();
  const debts = calculateDebts(expenses, members, settledDebts);
  const myShare = expenses.reduce((sum, e) => sum + (e.splits?.[uid] || 0), 0);
  const iOwe    = debts.filter(d => d.from === uid).reduce((s, d) => s + d.amount, 0);
  const owedToMe = debts.filter(d => d.to === uid).reduce((s, d) => s + d.amount, 0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .ex-root { min-height: 100vh; background: #FAF6F0; font-family: 'DM Sans', sans-serif; padding-bottom: 40px; }
        .ex-nav { background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
        .ex-back { font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.55); cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; }
        .ex-back:hover { color: #F5A623; }
        .ex-nav-title { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 600; color: #1a1714; }
        .ex-add-btn { font-size: 12px; font-weight: 500; color: white; background: #F5A623; border: none; border-radius: 100px; padding: 7px 16px; cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .ex-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ex-hero { background: #1E2D4A; padding: 24px; }
        .ex-hero-label { font-size: 11px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
        .ex-hero-amount { font-family: 'Playfair Display', serif; font-size: 40px; font-weight: 600; color: white; margin-bottom: 4px; }
        .ex-hero-sub { font-size: 13px; color: rgba(255,255,255,0.5); margin-bottom: 14px; }
        .ex-hero-stats { display: flex; gap: 8px; flex-wrap: wrap; }
        .ex-hero-stat { flex: 1; min-width: 80px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 10px 12px; }
        .ex-hero-stat-label { font-size: 10px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
        .ex-hero-stat-val { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 600; color: white; }
        .ex-hero-stat-val.amber { color: #F5A623; }
        .ex-hero-stat-val.green { color: #4ECDA4; }
        .ex-tabs { display: flex; background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); }
        .ex-tab { flex: 1; padding: 14px; text-align: center; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.45); cursor: pointer; border: none; background: none; font-family: 'DM Sans', sans-serif; border-bottom: 2px solid transparent; transition: all 0.2s; }
        .ex-tab.active { color: #F5A623; border-bottom-color: #F5A623; }
        .ex-content { padding: 20px; }
        .ex-empty { background: white; border-radius: 16px; padding: 32px; text-align: center; border: 1.5px dashed rgba(26,23,20,0.12); }
        .ex-empty-icon { font-size: 32px; margin-bottom: 10px; }
        .ex-empty-title { font-size: 15px; font-weight: 500; color: #1a1714; margin-bottom: 6px; }
        .ex-empty-sub { font-size: 13px; color: rgba(26,23,20,0.45); margin-bottom: 16px; }
        .ex-empty-btn { padding: 10px 20px; background: #F5A623; color: white; border: none; border-radius: 100px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .ex-expense { background: white; border-radius: 14px; border: 0.5px solid rgba(26,23,20,0.08); padding: 16px; margin-bottom: 10px; }
        .ex-expense-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
        .ex-expense-desc { font-size: 14px; font-weight: 500; color: #1a1714; }
        .ex-expense-amount { font-size: 16px; font-weight: 600; color: #1a1714; font-family: 'Playfair Display', serif; }
        .ex-expense-meta { font-size: 12px; color: rgba(26,23,20,0.45); margin-bottom: 8px; }
        .ex-expense-splits { display: flex; flex-wrap: wrap; gap: 6px; }
        .ex-split-tag { font-size: 11px; padding: 3px 8px; border-radius: 100px; background: #FAF6F0; color: rgba(26,23,20,0.6); }
        .ex-delete-btn { background: none; border: none; font-size: 14px; color: rgba(26,23,20,0.25); cursor: pointer; }
        .ex-delete-btn:hover { color: #c9622f; }
        .ex-balance-card { background: white; border-radius: 14px; border: 0.5px solid rgba(26,23,20,0.08); padding: 16px; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; }
        .ex-balance-name { font-size: 14px; font-weight: 500; color: #1a1714; }
        .ex-balance-amount { font-size: 15px; font-weight: 600; font-family: 'Playfair Display', serif; }
        .ex-balance-positive { color: #1D9E75; }
        .ex-balance-negative { color: #c9622f; }
        .ex-balance-zero { color: rgba(26,23,20,0.4); }
        .ex-overlay { position: fixed; inset: 0; background: rgba(26,23,20,0.5); z-index: 200; display: flex; align-items: flex-end; justify-content: center; }
        .ex-modal { background: white; border-radius: 20px 20px 0 0; padding: 28px 24px; width: 100%; max-width: 600px; max-height: 85vh; overflow-y: auto; animation: slideUp 0.3s ease; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .ex-modal-title { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: #1a1714; margin-bottom: 20px; }
        .ex-field { margin-bottom: 14px; }
        .ex-label { display: block; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.55); margin-bottom: 5px; }
        .ex-input { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; }
        .ex-input:focus { border-color: #F5A623; background: white; }
        .ex-select { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; cursor: pointer; appearance: auto; }
        .ex-select:focus { border-color: #F5A623; background: white; }
        .ex-split-options { display: flex; gap: 8px; }
        .ex-split-opt { flex: 1; padding: 10px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: white; font-size: 13px; font-weight: 500; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; text-align: center; transition: all 0.15s; }
        .ex-split-opt.selected { background: #F5A623; color: white; border-color: #F5A623; }
        .ex-custom-splits { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
        .ex-custom-row { display: flex; align-items: center; gap: 10px; }
        .ex-custom-name { font-size: 13px; color: #1a1714; flex: 1; }
        .ex-custom-input { width: 80px; padding: 8px 10px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 8px; background: #fafaf8; font-size: 13px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; text-align: right; }
        .ex-submit-btn { width: 100%; padding: 14px; background: #F5A623; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; margin-top: 8px; }
        .ex-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ex-cancel-btn { width: 100%; padding: 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; margin-top: 8px; }

        /* Settle Up section */
        .ex-settle-header { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: #1a1714; margin-bottom: 12px; letter-spacing: -0.02em; }
        .ex-settle-divider { height: 1px; background: rgba(26,23,20,0.08); margin: 24px 0 20px; }
        .ex-all-square { background: #FEF0DC; border-radius: 14px; padding: 18px 20px; text-align: center; font-size: 15px; font-weight: 500; color: #C47A0A; }
        .ex-debt-card { background: white; border-radius: 14px; border: 0.5px solid rgba(26,23,20,0.08); padding: 14px 16px; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; gap: 12px; transition: box-shadow 0.15s; }
        .ex-debt-card:hover { box-shadow: 0 2px 12px rgba(26,23,20,0.06); }
        .ex-debt-names { font-size: 14px; font-weight: 500; color: #1a1714; margin-bottom: 3px; }
        .ex-debt-amount { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 600; }
        .ex-settle-btn { flex-shrink: 0; padding: 8px 14px; background: #1E2D4A; color: white; border: none; border-radius: 100px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; white-space: nowrap; transition: background 0.15s; }
        .ex-settle-btn:hover:not(:disabled) { background: #F5A623; color: #1E2D4A; }
        .ex-settle-btn:disabled { opacity: 0.55; cursor: not-allowed; }
      `}</style>

      <div className="ex-root">
        <div className="ex-nav">
          <button className="ex-back" onClick={onBack}>← Back</button>
          <span className="ex-nav-title">{trip.name}</span>
          <button className="ex-add-btn" onClick={() => setShowForm(true)} disabled={!membersLoaded}>
            {membersLoaded ? "+ Add expense" : "Loading..."}
          </button>
        </div>

        {/* ── Hero with personal balance summary ── */}
        <div className="ex-hero">
          <div className="ex-hero-label">Total spent</div>
          <div className="ex-hero-amount">${totalSpent.toFixed(2)}</div>
          <div className="ex-hero-sub">
            {expenses.length} expense{expenses.length !== 1 ? "s" : ""} · {memberUids.length} traveler{memberUids.length !== 1 ? "s" : ""}
          </div>
          {expenses.length > 0 && (
            <div className="ex-hero-stats">
              <div className="ex-hero-stat">
                <div className="ex-hero-stat-label">Your share</div>
                <div className="ex-hero-stat-val">${myShare.toFixed(2)}</div>
              </div>
              {iOwe > 0.01 && (
                <div className="ex-hero-stat">
                  <div className="ex-hero-stat-label">You owe</div>
                  <div className="ex-hero-stat-val amber">${iOwe.toFixed(2)}</div>
                </div>
              )}
              {owedToMe > 0.01 && (
                <div className="ex-hero-stat">
                  <div className="ex-hero-stat-label">You are owed</div>
                  <div className="ex-hero-stat-val green">${owedToMe.toFixed(2)}</div>
                </div>
              )}
              {iOwe < 0.01 && owedToMe < 0.01 && expenses.length > 0 && (
                <div className="ex-hero-stat">
                  <div className="ex-hero-stat-label">Balance</div>
                  <div className="ex-hero-stat-val green">All square ✓</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ex-tabs">
          <button className={`ex-tab ${activeTab === "expenses" ? "active" : ""}`} onClick={() => setActiveTab("expenses")}>Expenses</button>
          <button className={`ex-tab ${activeTab === "balances" ? "active" : ""}`} onClick={() => setActiveTab("balances")}>Who owes what</button>
        </div>

        <div className="ex-content">

          {/* ── Expenses tab ── */}
          {activeTab === "expenses" && (
            loading ? (
              <p style={{textAlign:'center',color:'rgba(26,23,20,0.4)',fontSize:14,paddingTop:20}}>Loading...</p>
            ) : expenses.length === 0 ? (
              <div className="ex-empty">
                <div className="ex-empty-icon">💸</div>
                <div className="ex-empty-title">No expenses yet</div>
                <div className="ex-empty-sub">Add your first expense and split it with the group.</div>
                <button className="ex-empty-btn" onClick={() => setShowForm(true)}>Add first expense</button>
              </div>
            ) : (
              <>
                {deleteError && (
                  <p style={{fontSize:13,color:'#c9622f',marginBottom:10}}>{deleteError}</p>
                )}
                {/* Expense list */}
                {expenses.map(exp => (
                  <div key={exp.id} className="ex-expense">
                    <div className="ex-expense-top">
                      <div>
                        <div className="ex-expense-desc">{exp.description}</div>
                        <div className="ex-expense-meta">Paid by {exp.paidByName}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div className="ex-expense-amount">${exp.amount?.toFixed(2)}</div>
                        {exp.createdByUid === uid && (
                          <button className="ex-delete-btn" onClick={() => handleDelete(exp.id)}>×</button>
                        )}
                      </div>
                    </div>
                    <div className="ex-expense-splits">
                      {Object.entries(exp.splits || {}).map(([uid, amount]) => (
                        <span key={uid} className="ex-split-tag">
                          {getMemberName(uid)}: ${parseFloat(amount).toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}

                {/* ── Settle Up section ── */}
                <div className="ex-settle-divider" />
                <div className="ex-settle-header">Settle Up 💸</div>

                {settleError && (
                  <p style={{fontSize:13,color:'#c9622f',marginBottom:10}}>{settleError}</p>
                )}
                {debts.length === 0 ? (
                  <div className="ex-all-square">You're all square! 🎉</div>
                ) : (
                  debts.map((debt, i) => {
                    const isMyDebt   = debt.from === uid;
                    const isOwedToMe = debt.to   === uid;
                    const isInvolved = isMyDebt || isOwedToMe;
                    const debtKey    = `${debt.from}_${debt.to}`;
                    const isSettling = settling === debtKey;

                    let borderColor = "transparent";
                    if (isMyDebt)   borderColor = "#F5A623";
                    if (isOwedToMe) borderColor = "#1E2D4A";

                    return (
                      <div
                        key={i}
                        className="ex-debt-card"
                        style={{ borderLeft: `3px solid ${borderColor}` }}
                      >
                        <div>
                          <div className="ex-debt-names">
                            {isMyDebt
                              ? `You owe ${debt.toName}`
                              : isOwedToMe
                                ? `${debt.fromName} owes you`
                                : `${debt.fromName} owes ${debt.toName}`}
                          </div>
                          <div
                            className="ex-debt-amount"
                            style={{ color: isMyDebt ? "#C47A0A" : isOwedToMe ? "#1D9E75" : "#1a1714" }}
                          >
                            ${debt.amount.toFixed(2)}
                          </div>
                        </div>
                        {isInvolved && (
                          <button
                            className="ex-settle-btn"
                            onClick={() => handleSettle(debt)}
                            disabled={isSettling}
                          >
                            {isSettling ? "Saving..." : "Mark as Settled ✓"}
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            )
          )}

          {/* ── Who owes what tab (unchanged) ── */}
          {activeTab === "balances" && (
            Object.keys(balances).length === 0 ? (
              <div className="ex-empty">
                <div className="ex-empty-icon">✅</div>
                <div className="ex-empty-title">All settled up!</div>
                <div className="ex-empty-sub">Add some expenses first.</div>
              </div>
            ) : Object.entries(balances).map(([uid, balance]) => (
              <div key={uid} className="ex-balance-card">
                <div>
                  <div className="ex-balance-name">{getMemberName(uid)}</div>
                  <div style={{fontSize:12,color:'rgba(26,23,20,0.4)'}}>
                    {balance > 0.01 ? "is owed money" : balance < -0.01 ? "owes money" : "is settled up ✓"}
                  </div>
                </div>
                <div className={`ex-balance-amount ${balance > 0.01 ? "ex-balance-positive" : balance < -0.01 ? "ex-balance-negative" : "ex-balance-zero"}`}>
                  {balance > 0.01 ? "+" : ""}{balance.toFixed(2)}
                </div>
              </div>
            ))
          )}

        </div>
      </div>

      {/* ── Add expense modal (unchanged) ── */}
      {showForm && (
        <div className="ex-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="ex-modal">
            <div className="ex-modal-title">Add expense 💸</div>

            <div className="ex-field">
              <label className="ex-label">Description</label>
              <input className="ex-input" placeholder="e.g. Dinner at Nobu" value={form.description}
                onChange={e => setForm({...form, description: e.target.value})} />
            </div>

            <div className="ex-field">
              <label className="ex-label">Amount ($)</label>
              <input className="ex-input" type="number" placeholder="0.00" value={form.amount}
                onChange={e => setForm({...form, amount: e.target.value})} />
            </div>

            <div className="ex-field">
              <label className="ex-label">Category</label>
              <select className="ex-select" value={form.category}
                onChange={e => setForm({...form, category: e.target.value})}>
                {["Food", "Transport", "Accommodation", "Activities", "Shopping", "Other"].map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="ex-field">
              <label className="ex-label">Paid by ({members.length} members loaded)</label>
              <select className="ex-select" value={form.paidBy}
                onChange={e => setForm({...form, paidBy: e.target.value})}>
                {members.map(m => (
                  <option key={m.uid} value={m.uid}>
                    {m.name}{m.uid === uid ? " (you)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="ex-field">
              <label className="ex-label">Split</label>
              <div className="ex-split-options">
                <button className={`ex-split-opt ${form.splitType === "equal" ? "selected" : ""}`}
                  onClick={() => setForm({...form, splitType: "equal"})}>Equal</button>
                <button className={`ex-split-opt ${form.splitType === "custom" ? "selected" : ""}`}
                  onClick={() => setForm({...form, splitType: "custom"})}>Custom</button>
              </div>

              {form.splitType === "equal" && memberUids.length > 0 && (
                <div style={{marginTop:10,padding:'10px 14px',background:'#FAF6F0',borderRadius:10}}>
                  <div style={{fontSize:12,color:'rgba(26,23,20,0.5)'}}>
                    Split equally between {memberUids.length} people
                    {form.amount ? ` — $${(parseFloat(form.amount) / memberUids.length).toFixed(2)} each` : ""}
                  </div>
                </div>
              )}

              {form.splitType === "custom" && (
                <div className="ex-custom-splits">
                  {members.map(m => (
                    <div key={m.uid} className="ex-custom-row">
                      <span className="ex-custom-name">{m.name}{m.uid === uid ? " (you)" : ""}</span>
                      <input className="ex-custom-input" type="number" placeholder="0.00"
                        value={form.customSplits[m.uid] || ""}
                        onChange={e => setForm({...form, customSplits: {...form.customSplits, [m.uid]: parseFloat(e.target.value) || 0}})} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {addError && <p style={{fontSize:13,color:'#c9622f',marginBottom:8}}>{addError}</p>}
            <button className="ex-submit-btn" onClick={handleAddExpense}
              disabled={submitting || !form.description || !form.amount}>
              {submitting ? "Adding..." : "Add expense →"}
            </button>
            <button className="ex-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}
